import React from 'react';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Tooltip, Legend, Filler,
} from 'chart.js';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import { Box } from '@mui/material';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Tooltip, Legend, Filler);

ChartJS.defaults.font.family = 'Inter, sans-serif';
ChartJS.defaults.color = '#6B7280';

const gy = { grid: { color: '#F1F3F7' }, border: { display: false } } as any;
const gx = { grid: { display: false }, border: { display: false } } as any;

/** Fixed-height responsive chart wrapper. */
const ChartBox: React.FC<{ height?: number; children: React.ReactNode }> = ({ height = 280, children }) => (
  <Box sx={{ position: 'relative', height }}>{children}</Box>
);

export interface LineSeries {
  label?: string;
  data: number[];
  color: string;
  fill?: boolean;
  dashed?: boolean;
}

export const LineChart: React.FC<{ labels: string[]; series: LineSeries[]; height?: number; legend?: boolean }> = ({
  labels, series, height, legend = false,
}) => (
  <ChartBox height={height}>
    <Line
      data={{
        labels,
        datasets: series.map((s) => ({
          label: s.label,
          data: s.data,
          borderColor: s.color,
          backgroundColor: s.fill ? hexToRgba(s.color, 0.12) : s.color,
          fill: !!s.fill,
          tension: 0.38,
          borderWidth: 2.5,
          pointRadius: 0,
          borderDash: s.dashed ? [5, 4] : undefined,
        })),
      }}
      options={{
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: legend, position: 'top', align: 'end' } },
        scales: { y: gy, x: gx },
      }}
    />
  </ChartBox>
);

export const DoughnutChart: React.FC<{ labels: string[]; data: number[]; colors: string[]; height?: number }> = ({
  labels, data, colors, height = 220,
}) => (
  <ChartBox height={height}>
    <Doughnut
      data={{ labels, datasets: [{ data, backgroundColor: colors, borderWidth: 0 }] }}
      options={{ responsive: true, maintainAspectRatio: false, cutout: '64%', plugins: { legend: { display: false } } }}
    />
  </ChartBox>
);

export const BarChart: React.FC<{ labels: string[]; data: number[]; color?: string; height?: number }> = ({
  labels, data, color = '#076FF7', height = 220,
}) => (
  <ChartBox height={height}>
    <Bar
      data={{ labels, datasets: [{ data, backgroundColor: color, borderRadius: 6, maxBarThickness: 26 }] }}
      options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: gy, x: gx } }}
    />
  </ChartBox>
);

/** Legend chips below a chart. */
export const ChartLegend: React.FC<{ items: { label: string; color: string }[] }> = ({ items }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.75, mt: 1.5 }}>
    {items.map((it) => (
      <Box key={it.label} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 12.5, color: 'text.secondary' }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: it.color }} />
        {it.label}
      </Box>
    ))}
  </Box>
);

function hexToRgba(hex: string, a: number): string {
  const m = hex.replace('#', '');
  const r = parseInt(m.substring(0, 2), 16);
  const g = parseInt(m.substring(2, 4), 16);
  const b = parseInt(m.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}
